import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FilmSlate } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) { nav("/"); return null; }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain flex items-center justify-center p-6 relative">
      <div className="absolute top-6 left-8 flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-[#D97706] flex items-center justify-center">
          <FilmSlate size={18} weight="duotone" color="#0A0A0A" />
        </div>
        <div>
          <div className="text-[15px] font-extrabold tracking-tight leading-none">Motographist</div>
          <div className="text-[10px] text-white/40 mt-0.5 tracking-widest uppercase">Ops</div>
        </div>
      </div>

      <div className="w-full max-w-[400px] fade-up relative z-10">
        <div className="mb-8">
          <div className="text-xs text-[#D97706] uppercase tracking-widest mb-3">Sign in</div>
          <h1 className="text-4xl font-extrabold tracking-tight leading-[1.05]">
            Run your <br /> production house<br />
            <span className="text-white/40">like software.</span>
          </h1>
          <p className="text-sm text-white/50 mt-4 max-w-[340px]">
            Manage projects, crew, deliverables and finance from a single dark control center.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/40">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-white/30 focus:outline-none transition-colors"
              placeholder="you@studio.com"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/40">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-white/30 focus:outline-none transition-colors"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-white text-black font-semibold text-sm rounded-md py-2.5 hover:bg-white/90 transition-colors active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign in →"}
          </button>
        </form>

        <div className="mt-6 text-xs text-white/50 text-center">
          Don't have an account?{" "}
          <Link to="/signup" data-testid="link-to-signup" className="text-[#D97706] hover:text-[#F59E0B] font-medium">Create one</Link>
        </div>
      </div>
    </div>
  );
}
